import { API_BASE, buildQuery, request } from './api-client';
import type {
  CollectionOption,
  CouponDetail,
  CouponListFilters,
  CouponSummary,
  CouponUsage,
  CreateCouponInput,
  CursorPage,
  UpdateCouponInput,
} from '../types';

const BASE = `${API_BASE}/admin/coupons`;
const PAGE_SIZE = 20;

export function listCoupons(filters: CouponListFilters, cursor?: string) {
  return request<CursorPage<CouponSummary>>(`${BASE}${buildQuery({ ...filters, limit: PAGE_SIZE, cursor })}`);
}

export function getCoupon(id: string) {
  return request<CouponDetail>(`${BASE}/${id}`);
}

export function createCoupon(input: CreateCouponInput) {
  return request<CouponDetail>(BASE, { method: 'POST', body: JSON.stringify(input) });
}

export function updateCoupon(id: string, input: UpdateCouponInput) {
  return request<CouponDetail>(`${BASE}/${id}`, { method: 'PATCH', body: JSON.stringify(input) });
}

export function activateCoupon(id: string) {
  return request<CouponDetail>(`${BASE}/${id}/activate`, { method: 'POST' });
}

export function deactivateCoupon(id: string, reason?: string) {
  return request<CouponDetail>(`${BASE}/${id}/deactivate`, {
    method: 'POST',
    body: JSON.stringify(reason ? { reason } : {}),
  });
}

export function listCouponUsage(id: string, cursor?: string) {
  return request<CursorPage<CouponUsage>>(`${BASE}/${id}/usage${buildQuery({ limit: PAGE_SIZE, cursor })}`);
}

/** Scope picker source — the existing admin Collections list, no coupon-specific endpoint. */
export async function listCollectionOptions(): Promise<CollectionOption[]> {
  // ponytail: first 100 Collections only; add server search to the picker if the catalog outgrows it.
  const page = await request<{ items: CollectionOption[] }>(
    `${API_BASE}/admin/collections${buildQuery({ limit: 100, sortBy: 'displayOrder', order: 'asc' })}`,
  );
  // The admin list has one row per price *version* of a Collection (same code); coupons scope by code.
  const byCode = new Map<string, CollectionOption>();
  for (const { code, displayName, isActive } of page.items) {
    const seen = byCode.get(code);
    byCode.set(code, { code, displayName, isActive: isActive || !!seen?.isActive });
  }
  return [...byCode.values()];
}
